import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FiArrowLeft, FiX } from "react-icons/fi";
import {
  authenticatePublicUser,
  persistAuthSession,
  registerPublicUser,
} from "../services/Auth";
import { chatLoader } from "../services/syncLoader";

const AuthModal = ({ isOpen, mode, onClose, onSwitchMode }) => {
  const translation = useSelector((state) => state.translation);
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });
  const [registerForm, setRegisterForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phoneNumber: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  if (!isOpen) return null;

  const isRegister = mode === "register";

  const updateLoginForm = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateRegisterForm = (event) => {
    const { name, value } = event.target;
    setRegisterForm((prev) => ({ ...prev, [name]: value }));
  };

  const getErrorMessage = (error) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong. Please try again."
    );
  };

  const closeModal = () => {
    setMessage("");
    onClose();
  };

  const switchMode = (nextMode) => {
    setMessage("");
    onSwitchMode(nextMode);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await authenticatePublicUser(loginForm);
      persistAuthSession(response.data);
      chatLoader();
      setMessage(translation.auth_login_success || "Login successful.");
      closeModal();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await registerPublicUser(registerForm);
      persistAuthSession(response.data);
      chatLoader();
      setMessage(
        translation.auth_register_success || "Registration successful."
      );
      closeModal();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={closeModal}>
      <div
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-modal-top">
          <button className="auth-back" onClick={closeModal}>
            <FiArrowLeft /> Back
          </button>
          <button className="auth-close" onClick={closeModal}>
            <FiX />
          </button>
        </div>

        {isRegister ? (
          <form onSubmit={handleRegister}>
            <h3 className="auth-title">
              {translation.auth_register_title || "Create your account"}
            </h3>
            <p className="auth-subtitle">
              {translation.auth_register_subtitle ||
                "Create your account to get started with eSyncing."}
            </p>
            <div className="auth-grid">
              <label>
                {translation.auth_first_name_label || "First name"}
                <input
                  type="text"
                  name="firstName"
                  value={registerForm.firstName}
                  onChange={updateRegisterForm}
                  required
                />
              </label>
              <label>
                {translation.auth_last_name_label || "Last name"}
                <input
                  type="text"
                  name="lastName"
                  value={registerForm.lastName}
                  onChange={updateRegisterForm}
                  required
                />
              </label>
            </div>
            <label>
              {translation.auth_work_email_label || "Email"}
              <input
                type="email"
                name="email"
                value={registerForm.email}
                onChange={updateRegisterForm}
                placeholder={
                  translation.auth_work_email_placeholder || "you@company.com"
                }
                required
              />
            </label>
            <label>
              {translation.auth_password_label || "Password"}
              <input
                type="password"
                name="password"
                value={registerForm.password}
                onChange={updateRegisterForm}
                minLength="4"
                required
              />
            </label>
            <label>
              {translation.auth_phone_label || "Phone number"}
              <input
                type="text"
                name="phoneNumber"
                value={registerForm.phoneNumber}
                onChange={updateRegisterForm}
                required
              />
            </label>
            {message && <div className="auth-message">{message}</div>}
            <button className="auth-primary" disabled={isSubmitting}>
              {isSubmitting
                ? translation.auth_submitting || "Please wait..."
                : translation.auth_register_button || "Create account"}
            </button>
            <div className="auth-switch">
              {translation.auth_register_switch_prompt ||
                "Already have an account?"}{" "}
              <button type="button" onClick={() => switchMode("login")}>
                {translation.auth_register_switch_action || "Sign in"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <h3 className="auth-title">
              {translation.auth_login_title || "Let's get you started"}
            </h3>
            <p className="auth-subtitle">
              {translation.auth_login_subtitle ||
                "Enter your email and password to access the panel."}
            </p>
            <label>
              {translation.auth_work_email_label || "Email"}
              <input
                type="email"
                name="email"
                value={loginForm.email}
                onChange={updateLoginForm}
                placeholder={
                  translation.auth_work_email_placeholder || "you@company.com"
                }
                required
              />
            </label>
            <label>
              {translation.auth_password_label || "Password"}
              <input
                type="password"
                name="password"
                value={loginForm.password}
                onChange={updateLoginForm}
                required
              />
            </label>
            {message && <div className="auth-message">{message}</div>}
            <button className="auth-primary" disabled={isSubmitting}>
              {isSubmitting
                ? translation.auth_submitting || "Please wait..."
                : translation.auth_login_button || "Sign in"}
            </button>
            <div
              className="auth-footnote"
              dangerouslySetInnerHTML={{
                __html:
                  translation.auth_terms_notice ||
                  'By continuing, you agree to our <a href="/terms-conditions">Terms</a> &amp; <a href="/privacy-policy">Privacy Policy</a>.',
              }}
            />
            <div className="auth-switch">
              {translation.auth_switch_prompt || "Don't have an account?"}{" "}
              <button type="button" onClick={() => switchMode("register")}>
                {translation.auth_switch_action || "Create account"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
